import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useState, useRef } from "react";
import classnames from "classnames";
import PictureWithLinkComponent from "components/core/picture-with-link/picture-with-link-component";
import { ROOT_LINK, MONDAY_LOGO_SRC } from "constants/links";
import { HEADER_POPUP_DATA } from "constants/header-popup";
import PictureComponent from "components/core/picture/picture-component";
import { POPUP_LINK_CLICKED } from "/constants/bigbrain-event-types";
import { trackEvent } from "/services/bigbrain-service";
import { withGenericConfig } from "/contexts/generic-config-context";
import { fireGtmEvent } from "client/services/gtm-service/gtm-service";
import DataLayerEventTypes from "constants/data-layer-event-types";
import { StyledBasicHeaderComponent } from "./basic-header-component.scss";
const MONDAY_LOGO_DARK_MODE = "https://dapulse-res.cloudinary.com/image/upload/Generator_featured%20images/Homepage%20-%202024/monday-logo-white.png";
const BasicHeaderComponent = /*#__PURE__*/ forwardRef((props, ref)=>{
    const [isScrolled, setIsScrolled] = useState(false);
    const [shadowForced, setShadowForced] = useState(false);
    const [isDynamicColor, setisDynamicColor] = useState(props === null || props === void 0 ? void 0 : props.shouldUseDynamicColor);
    const [isHover, setIsHover] = useState(false);
    const [isOnTop, setIsOnTop] = useState(true);
    const { children, showMenu, imageSrc, imageHref, imageStyle, className, secondaryLogoObject, imageAlt, logoPopup, translate, isMenuOpen, localeId, trackScroll, shouldUseDynamicColor, onDynamicColorChange = ()=>{} } = props;
    // we use this because window.scrollY is always 0.
    const htmlContainer = useRef(null);
    useEffect(()=>{
        window.addEventListener("scroll", handleScroll);
        htmlContainer.current = document.getElementById("main-html");
        return ()=>window.removeEventListener("scroll", handleScroll);
    }, []);
    useEffect(()=>{
        updateIsDynamicColor(getisDynamicColor());
    }, [
        isHover,
        isMenuOpen,
        isOnTop
    ]);
    const updateIsDynamicColor = (newVal)=>{
        setisDynamicColor(newVal);
        onDynamicColorChange(newVal);
    };
    const handleScroll = ()=>{
        var _htmlContainer_current;
        setIsScrolled(trackScroll && window.scrollY > 0);
        setIsOnTop((htmlContainer === null || htmlContainer === void 0 ? void 0 : (_htmlContainer_current = htmlContainer.current) === null || _htmlContainer_current === void 0 ? void 0 : _htmlContainer_current.scrollTop) === 0);
    };
    const getisDynamicColor = ()=>{
        return shouldUseDynamicColor && isOnTop && !isHover && !isMenuOpen;
    };
    // we use onMouseEnter and onMouseLeave to handle the dark mode on hover because we change images urls
    const onMouseEnter = ()=>{
        setIsHover(true);
    };
    const onMouseLeave = ()=>{
        setIsHover(false);
    };
    const forceShadow = ()=>{
        setShadowForced(true);
    };
    const unforceShadow = ()=>{
        setShadowForced(false);
    };
    const renderSecondaryLogoIfNeeded = ()=>{
        if (!secondaryLogoObject) return;
        return /*#__PURE__*/ _jsx("div", {
            className: "secondary-logo-wrapper",
            children: /*#__PURE__*/ _jsx(PictureWithLinkComponent, {
                src: secondaryLogoObject.src,
                className: "base-header-monday-logo-image",
                href: secondaryLogoObject.url,
                alt: secondaryLogoObject.alt
            })
        });
    };
    const onPopupLinkClicked = (link)=>{
        trackEvent(POPUP_LINK_CLICKED, {
            kind: link
        });
    };
    const getPopupData = (logoPopup)=>{
        if (!logoPopup) return null;
        return HEADER_POPUP_DATA[logoPopup];
    };
    const renderPopupIfNeeded = ()=>{
        const logoPopupData = getPopupData(logoPopup);
        if (isMenuOpen || !logoPopupData) return null;
        const link = logoPopupData === null || logoPopupData === void 0 ? void 0 : logoPopupData.getLink(localeId);
        return /*#__PURE__*/ _jsxs("div", {
            className: classnames("logo-popup", logoPopupData === null || logoPopupData === void 0 ? void 0 : logoPopupData.popupClassName),
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "popup-text",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "text",
                            dangerouslySetInnerHTML: {
                                __html: translate(logoPopupData === null || logoPopupData === void 0 ? void 0 : logoPopupData.textKey)
                            }
                        }),
                        /*#__PURE__*/ _jsx("a", {
                            href: link,
                            className: "link-text",
                            target: "_blank",
                            rel: "noreferrer",
                            onClick: ()=>onPopupLinkClicked(link),
                            children: translate(logoPopupData === null || logoPopupData === void 0 ? void 0 : logoPopupData.ctaKey)
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "popup-gif",
                    children: /*#__PURE__*/ _jsx(PictureComponent, {
                        src: logoPopupData === null || logoPopupData === void 0 ? void 0 : logoPopupData.gifSrc,
                        pictureClass: "popup-gif-image",
                        specificImageStyle: {
                            height: "42px"
                        }
                    })
                })
            ]
        });
    };
    const onMainLogoClick = ()=>{
        fireGtmEvent(DataLayerEventTypes.CLICK_MAIN_NAV, {
            click_text: "logo"
        });
    };
    const handelKeyDown = (event)=>{
        event.key === "Enter" && onMainLogoClick();
    };
    const isWithShadow = isScrolled || shadowForced;
    useImperativeHandle(ref, ()=>({
            forceShadow,
            unforceShadow
        }));
    return /*#__PURE__*/ _jsx(StyledBasicHeaderComponent, {
        className: classnames("basic-header-component", className, {
            "with-secondary-logo": !!secondaryLogoObject,
            scrolled: isScrolled,
            "dynamic-color": isDynamicColor
        }),
        onMouseEnter: onMouseEnter,
        onMouseLeave: onMouseLeave,
        children: /*#__PURE__*/ _jsx("div", {
            className: classnames("basic-header-content-wrapper", {
                "with-shadow": isWithShadow
            }),
            children: /*#__PURE__*/ _jsxs("div", {
                className: "basic-header-content",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "main-logo-wrapper",
                        onClick: onMainLogoClick,
                        onKeyDown: handelKeyDown,
                        children: [
                            /*#__PURE__*/ _jsx(PictureWithLinkComponent, {
                                src: isDynamicColor ? MONDAY_LOGO_DARK_MODE : imageSrc || MONDAY_LOGO_SRC,
                                className: "base-header-monday-logo-image",
                                href: imageHref || ROOT_LINK,
                                specificImageStyle: imageStyle,
                                alt: imageAlt
                            }),
                            renderPopupIfNeeded()
                        ]
                    }),
                    renderSecondaryLogoIfNeeded(),
                    showMenu && children
                ]
            })
        })
    });
});
BasicHeaderComponent.defaultProps = {
    showMenu: true,
    imageSrc: MONDAY_LOGO_SRC,
    imageHref: ROOT_LINK,
    imageAlt: "monday.com logo",
    secondaryLogoObject: null,
    isMenuOpen: false,
    trackScroll: true
};
export default withGenericConfig(BasicHeaderComponent);
